import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { TrackingScreenNamesEnum } from '../../../tracking/tracking.data';
import { FeedbackDataStore } from '../../../feedback-data/feedback-data.store';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  FeedbackScreenEnum,
  ICancelSubscriptionScreen,
  ScreenInputTypesEnum,
} from '../../../feedback-data/feedback-data.data';
import { filter, map, shareReplay } from 'rxjs/operators';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import { FormGroup } from '@angular/forms';
import { FeedbackDataBuilder } from '../../../feedback-data/feedback-data.builder';
import { FeedbackDataDispatcher } from '../../../feedback-data/feedback-data.dispatcher';
import { FeedbackTrackingObject } from '../../../tracking/models/objects/feedback-tracking-object.model';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import { I2gNgTrackingActionBuilder, ITrackingAction } from 'i2g-ng-tracking';

export const DIALOG_TYPE = 'cancel-subscription-feedback-dialog';

@Component({
  selector: 'app-cancel-subscription-feedback-dialog',
  templateUrl: './cancel-subscription-feedback-dialog.component.html',
  styleUrls: ['./cancel-subscription-feedback-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CancelSubscriptionFeedbackDialogComponent implements OnInit {
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() secondaryActionText: string;

  @Output() submitFeedback: EventEmitter<void> = new EventEmitter<void>();
  @Output() dismissFeedback: EventEmitter<void> = new EventEmitter<void>();

  private _focusableTrigger: BehaviorSubject<any> = new BehaviorSubject<any>(
    true
  );

  loadingSpinnerIcon: IIcon;
  trackingObject: FeedbackTrackingObject;
  submitTrackingAction: ITrackingAction;
  cancelTrackingAction: ITrackingAction;

  feedbackScreen$: Observable<ICancelSubscriptionScreen>;
  primaryActionText$: Observable<string>;
  secondaryActionText$: Observable<string>;
  title$: Observable<string>;
  subtitle$: Observable<string>;

  screenInputTypesEnum: typeof ScreenInputTypesEnum;
  formGroup$: Observable<FormGroup>;
  formFields$: Observable<any>;
  dialogType: string;
  focusableTrigger$: Observable<void> = this._focusableTrigger.asObservable();

  constructor(
    private _feedbackDataBuilder: FeedbackDataBuilder,
    private _feedbackDataDispatcher: FeedbackDataDispatcher,
    private _feedbackDataStore: FeedbackDataStore,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  ngOnInit() {
    this.dialogType = DIALOG_TYPE;
    this.trackingObject = this._trackingObjectBuilder.buildFeedback();
    this.screenInputTypesEnum = ScreenInputTypesEnum;
    this.loadingSpinnerIcon = {
      name: 'i2g-loader',
      size: IconSizesEnum.x_large,
    };
    this.submitTrackingAction = this._trackingActionBuilder.buildButtonTapped(
      'cta_submit',
      this.trackingScreenName
    );
    this.cancelTrackingAction = this._trackingActionBuilder.buildButtonTapped(
      'cta_cancel',
      this.trackingScreenName
    );

    // fetch dialog content from feedback API
    this._feedbackDataDispatcher.fetchScreen(
      FeedbackScreenEnum.cancel_subscription
    );

    this.feedbackScreen$ = this._feedbackDataStore
      .getScreenById(FeedbackScreenEnum.cancel_subscription)
      .pipe(
        filter((x) => !!x),
        map((screen) => screen[0]), // we have only one screen for this dialog)
        shareReplay(1)
      );

    this.title$ = this.feedbackScreen$.pipe(
      map((screen: ICancelSubscriptionScreen) => screen.modal_title.label)
    );

    this.subtitle$ = this.feedbackScreen$.pipe(
      map((screen: ICancelSubscriptionScreen) => screen.feedback_question.label)
    );

    this.primaryActionText$ = this.feedbackScreen$.pipe(
      map((screen: ICancelSubscriptionScreen) => screen.cta_submit.label)
    );

    this.secondaryActionText$ = this.feedbackScreen$.pipe(
      map((screen: ICancelSubscriptionScreen) => screen.cta_cancel.label)
    );

    // angular form control
    this.formGroup$ = this.feedbackScreen$.pipe(
      map((screen: ICancelSubscriptionScreen) =>
        this._feedbackDataBuilder.buildFormGroup(screen.inputs)
      )
    );

    // used for constructing the UI
    this.formFields$ = this.feedbackScreen$.pipe(
      map((screen: ICancelSubscriptionScreen) =>
        this._feedbackDataBuilder.buildFormFields(screen.inputs)
      )
    );
  }

  // identifier comes from backend
  buildRadioTappedTrackingAction(radioIdentifier: string) {
    return this._trackingActionBuilder.buildRadioTapped(
      radioIdentifier,
      this.trackingScreenName
    );
  }

  onSubmit(formGroup: FormGroup) {
    if (formGroup.invalid) {
      // mark all controls as dirty to force form validation
      Object.keys(formGroup.controls).forEach((controlKey) => {
        formGroup.controls[controlKey].markAsDirty();
      });

      return;
    }

    const feedback = this._feedbackDataBuilder.buildFormResponse(formGroup);

    // send feedback to backend
    this._feedbackDataDispatcher.sendFeedback({
      screen: FeedbackScreenEnum.cancel_subscription,
      feedback,
    });

    this.submitFeedback.emit();
    formGroup.reset();
  }

  closeDialog(formGroup: FormGroup) {
    formGroup.reset();

    this.dismissFeedback.emit();
  }
}
